import { useGSAP } from "@gsap/react";
import { CustomEase } from "gsap/dist/CustomEase";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";
import { gsap } from "gsap/dist/gsap";
import React, { useId, useRef } from "react";

import { SlidingCardBlock, SlidingCardsBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { MediaChooserBlock } from "../MediaChooserBlock";

import styles from "./index.module.scss";

gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(CustomEase);

const SlidingCard = React.forwardRef<HTMLDivElement, SlidingCardBlock>(
    ({ title, media, columns }, ref) => {
        const id = useId();

        return (
            <div ref={ref} className={styles.card}>
                {media && media.length > 0 ? (
                    <MediaChooserBlock
                        wrapperAttrs={{ className: styles.cardMedia }}
                        value={media}
                        type="background"
                    />
                ) : null}
                <div className={styles.cardContent}>
                    <h3 className={styles.cardTitle}>{title}</h3>
                    {columns && columns.length > 0 && (
                        <div className={styles.cardColumns}>
                            <div className={styles.cardColumnsInner}>
                                {columns.map(({ title, subtitle }, index) => (
                                    <div
                                        key={`${id}-${index}`}
                                        className={concatClassNames([
                                            styles.cardColumn,
                                            columns.length === 1
                                                ? styles.cardColumnSingle
                                                : undefined,
                                        ])}
                                    >
                                        {title && (
                                            <h4
                                                className={
                                                    styles.cardColumnTitle
                                                }
                                            >
                                                {title}
                                            </h4>
                                        )}
                                        {subtitle && <p>{subtitle}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    },
);

interface Props {
    value: Value;
}

export const SlidingCardsBlock = ({ value: { theme, cards } }: Props) => {
    const id = useId();
    const sectionRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

    useGSAP(() => {
        if (!sectionRef.current || cardRefs.current.length === 0) return;

        const cardElements = cardRefs.current;

        // Set all cards off-screen
        gsap.set(cardElements, { yPercent: 100 });
        // Then set the first one to normal
        gsap.set(cardElements[0], { yPercent: 0 });

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: sectionRef.current,
                start: "top top",
                end: `+=${cardElements.length * 100}%`,
                scrub: 0.3,
                fastScrollEnd: true,
                pin: true,
                snap: {
                    snapTo: "labelsDirectional",
                    duration: { min: 0.3, max: 0.9 },
                    delay: 0.1,
                    ease: CustomEase.create(
                        "custom",
                        "M0,0 C0,0 0.084,-0.026 0.172,0.166 0.291,0.429 0.354,0.62 0.558,0.821 0.639,0.901 0.713,0.937 0.808,0.97 0.879,0.995 1,1 1,1 ",
                    ),
                },
            },
        });

        // Card transition sequence: each card gets a label and animation step
        cardElements.forEach((element, index) => {
            const label = `card-${index}`;
            tl.addLabel(label);
            if (index === 0) return;
            tl.to(element, { yPercent: 0, ease: "none" }, label);
        });

        // If you don't have this, the last card doesn't snap right
        tl.addLabel("end", "+=0.001");

        return () => {
            tl.kill();
        };
    }, []);

    if (!cards) return null;

    return (
        <section ref={sectionRef} className={styles[theme ?? "match"]}>
            <div className={styles.inner}>
                {cards.map((card, index) => (
                    <SlidingCard
                        key={`${id}-${index}`}
                        ref={(element) => {
                            cardRefs.current[index] = element;
                        }}
                        {...card}
                    />
                ))}
            </div>
        </section>
    );
};
